import { Button } from "components/Button/Button";
import { FormComponentProps } from "components/Form/components/FormComponentProps";
import { Input } from "components/Form/components/Input/Input";
import { LazyResizeableCodeEditor as ResizeableCodeEditor } from "components/Form/lazyCodeEditorComponents";
import { IconCollection } from "components/Icon/IconCollection";
import Tooltip from "components/Tooltip/Tooltip";
import "./SwitchContent.scss";

/**
 * Encode a latin1 "binary" string (as produced by the file formDTO, which decodes
 * the base64 content via Buffer.toString("binary")) back to base64 for a data URL.
 */
const toBase64 = (binary: string): string => {
    try {
        return window.btoa(binary);
    } catch {
        return "";
    }
};

const DownloadOnlyPanel = ({ contentType }: { contentType?: string }) => (
    <div className="switchContentDownloadOnly">
        This file{contentType ? ` (${contentType})` : ""} cannot be displayed inline. Use the{" "}
        <strong>Download</strong> action to save it.
    </div>
);

export const SwitchContentFormComponent = ({
    input,
    meta,
    fieldProps,
    fieldAttr,
}: FormComponentProps) => {
    const {
        isContentShown,
        setContentShown,
        isContentAlwaysHidden,
        hiddenContentTooltip,
        content,
        fileDisplayCategory,
        fileContentType,
    } = fieldProps;

    const changeToContentField = () => {
        setContentShown?.(true);
    };

    const button = (
        <Button
            onClick={changeToContentField}
            icon={IconCollection.textBoxOutline}
            variation="white"
            label={"Show content"}
            disabled={isContentAlwaysHidden}
        />
    );

    // Type-adaptive rendering of file content
    // (operator-task-fe-file-mimetype-adaptive-display). An explicit `content`
    // override node still wins; otherwise branch on the resolved display category.
    const renderShownContent = () => {
        if (content) {
            return content;
        }
        if (fileDisplayCategory === "image") {
            const base64 = toBase64(input.value ?? "");
            return base64 ? (
                <div className="switchContentMedia">
                    <img
                        className="switchContentImage"
                        src={`data:${fileContentType ?? "image/*"};base64,${base64}`}
                        alt="File preview"
                    />
                </div>
            ) : null;
        }
        if (fileDisplayCategory === "download") {
            return <DownloadOnlyPanel contentType={fileContentType} />;
        }
        // "text" | "code" | undefined -> code editor driven by fieldProps.lang
        return (
            <ResizeableCodeEditor
                input={input}
                meta={{}}
                fieldProps={fieldProps}
                fieldAttr={fieldAttr}
            />
        );
    };

    return isContentShown ? (
        renderShownContent()
    ) : (
        <div className="switchContentWrapper">
            <Input fieldProps={fieldProps} fieldAttr={fieldAttr} input={input} meta={meta}>
                {isContentAlwaysHidden && hiddenContentTooltip ? (
                    <Tooltip overlay={hiddenContentTooltip} overlayStyle={{ width: "300px" }}>
                        {button}
                    </Tooltip>
                ) : (
                    button
                )}
            </Input>
        </div>
    );
};
